/**
 * The confirmation step in front of a destructive action.
 *
 * Written once and shared, because the alternative is each screen deciding for itself
 * whether losing data deserves a question — and the screens that skipped it were not the
 * ones where deleting mattered least, they were just the ones written in a hurry.
 *
 * Three properties are what make this a safeguard rather than decoration:
 *
 * 1. **The subject is named.** A dialog that asks "are you sure?" and nothing else moves
 *    the decision without informing it: the operator confirms the dialog, not the file.
 *    Passing `subject` puts the actual file name in front of them.
 * 2. **Confirm is not the default focus.** Radix focuses the dialog's own close button
 *    first, so Enter on a reflex dismisses rather than destroys. Cancel also precedes
 *    confirm in the DOM, which keeps the keyboard path to the destructive button longer
 *    than the path away from it.
 * 3. **`loading` disables Cancel too.** Once the request is in flight the outcome is no
 *    longer the operator's to take back, and a Cancel that does nothing is a lie.
 *
 * Not built on a radix alert-dialog: the project does not depend on one, and the extra
 * modality it buys (no dismiss-on-outside-click) is not worth a new dependency here —
 * dismissing this dialog is the safe outcome, so making dismissal harder protects nothing.
 */

import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useI18n } from '@/lib/i18n';

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  /** What is about to be destroyed, verbatim — a file name, a policy number. */
  subject,
  /** Overrides the default "this cannot be undone" line when a sharper warning applies. */
  description,
  confirmLabel,
  onConfirm,
  loading,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subject?: string;
  description?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  loading?: boolean;
  /** Anything the confirmation should also collect — «Δεν ανανεώνει» asks for the reason. */
  children?: React.ReactNode;
}) {
  const { t } = useI18n();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description ?? t('confirm.cannotUndo')}</DialogDescription>
        </DialogHeader>

        {(subject || children) && (
          <DialogBody className="space-y-3">
            {subject && (
              <p
                className="border-border bg-muted/50 truncate rounded-lg border px-3 py-2.5 text-[13px] font-medium"
                title={subject}
              >
                {subject}
              </p>
            )}
            {children}
          </DialogBody>
        )}

        {/*
          `flex-col` cancels the footer's default `flex-col-reverse` on narrow screens.
          Reversing is right for an ordinary dialog — it lifts the primary action to where a
          thumb rests. It is wrong for this one: it put a red «Διαγραφή» directly under the
          thumb with «Ακύρωση» pushed below it, so the safest place to tap became the
          irreversible one and the confirmation step argued against itself. Cancel stays on
          top on a phone; from `sm` up the row order is unchanged.
        */}
        <DialogFooter className="flex-col sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t('action.cancel')}
          </Button>
          <Button variant="destructive" onClick={onConfirm} loading={loading}>
            {confirmLabel ?? t('action.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
